import { useTranslation } from 'react-i18next';
import Text from '../../components/atoms/text';
import Button from '../../components/atoms/button';
import Input from '../../components/atoms/input';
import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { NotificationToast } from '../../components/organisms/notification-toast';
import AuthService from '../../services/Auth.service';
import Regex from '../../utils/regex';
import Label from '../../components/atoms/text/label';
import AuthTemplate from '../../components/template/auth';

interface LoginForm {
	email: string;
	password: string;
	rememberMe: boolean;
}

const Login = () => {
	const [loginForm, setLoginForm] = useState<LoginForm>({
		email: '',
		password: '',
		rememberMe: false,
	});
	const [error, setError] = useState<string>('');
	const [loading, setLoading] = useState<boolean>(false);

	const navigate = useNavigate();
	const { t } = useTranslation();

	useEffect(() => {
		const errorMessage = sessionStorage.getItem('errorMessage');
		if (errorMessage) {
			setError(t(errorMessage));
			sessionStorage.removeItem('errorMessage');
		}
	}, []);

	// Exibe erros se existirem
	useEffect(() => {
		if (error) {
			NotificationToast.Warn(t(error));
			setError('');
		}
	}, [error]);

	// Lida com a mudança de input
	const handleInput = (e: React.ChangeEvent<HTMLInputElement>) => {
		const { name, value, checked, type } = e.target;
		setLoginForm((prev) => ({
			...prev,
			[name]: type === 'checkbox' ? checked : value,
		}));
	};

	// Valida o formulário e envia os dados
	const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
		e.preventDefault();

		// Validações
		if (!loginForm.email || !loginForm.password) {
			return setError('alert.error.mandatory');
		}
		if (!Regex.Email(loginForm.email)) {
			return setError('alert.error.email');
		}
		if (loginForm.password.length < 8) {
			return setError('alert.error.password');
		}

		setLoading(true);

		// Chama o serviço de login
		const response = await AuthService.login(
			loginForm.email,
			loginForm.password,
			loginForm.rememberMe,
			setError
		);

		setLoading(false);

		if (response) {
			navigate('/');
		}
	};

	return (
		<AuthTemplate title={t('login.title')}>
			<form
				className="flex flex-col gap-y-6 px-5 py-6 bg-[#ffffff3e] backdrop-blur-50 rounded-xl"
				onSubmit={handleSubmit}
			>
				<Text className="text-white" as="h2">
					{t('login.access')}
				</Text>
				<div className="flex flex-col gap-y-2">
					<Input
						name="email"
						className="w-72"
						placeholder={t('misc.email')}
						type="email"
						value={loginForm.email}
						onChange={handleInput}
						autoComplete="email"
					/>
					<Input
						name="password"
						placeholder={t('misc.password')}
						type="password"
						value={loginForm.password}
						onChange={handleInput}
						autoComplete="current-password"
					/>
				</div>
				<Label
					className="flex items-center gap-2 text-white w-fit cursor-pointer"
					htmlFor="rememberMe"
				>
					<Input
						className="accent-primary size-5 cursor-pointer"
						id="rememberMe"
						name="rememberMe"
						type="checkbox"
						checked={loginForm.rememberMe}
						onChange={handleInput}
					/>
					{t('login.remember_me')}
				</Label>
				<Button type="submit" loading={loading}>
					{t('login.sign_in')}
				</Button>
				<Link
					to="/reset_password"
					className="text-white cursor-pointer self-center"
				>
					{t('login.reset_password')}
				</Link>
			</form>
		</AuthTemplate>
	);
};

export default Login;
